/**
 * Docking clearance (M2 spec §7.2 step 1): a pure state machine `none → granted → expired`. A request within
 * `requestRange` of a station's hull (the caller passes `|p − centre| − radius`, scale build §3.3) is granted for that station's bay (one bay per station in M2) for
 * `clearanceSeconds` of game time; a grant replaces any earlier one, and a repeated request renews it. Once the
 * window runs out the state is `expired` until the player requests again.
 *
 * The runtime (M2-14) adds the checks this module cannot see (station model not ready, a hostile lock on the
 * player) before calling `requestClearance`, and reports every answer as a `CLEARANCE_REASON` in
 * `SEV.clearanceDenied`. Times are game time (`CareerMirror.gameTime`). Allocation-free.
 *
 * M5 (seam S-M2-11, plan C14): an installed `DockingPolicy` (the Charter's law: wanted levels, hostile standings) is
 * asked after the range check; a refusal returns its `DOCKING_POLICY_REASON` code, which the runtime reports in
 * `SEV.clearanceDenied` like M2's own reasons. With no policy installed, clearance is exactly M2's.
 */
import type { DockingConfig } from '../data/m2Schemas'
import { CLEARANCE_REASON } from '../space/events'

export const CLEARANCE_STATUS = { none: 0, granted: 1, expired: 2 } as const
export type ClearanceStatus = (typeof CLEARANCE_STATUS)[keyof typeof CLEARANCE_STATUS]
/** M5 refusal codes (S-M2-11), after M2's `CLEARANCE_REASON` values: wanted by the owner or the jurisdiction, or a hostile standing. */
export const DOCKING_POLICY_REASON = { wanted: 16, hostileStanding: 17 } as const
export type DockingPolicyReason = (typeof DOCKING_POLICY_REASON)[keyof typeof DOCKING_POLICY_REASON]
export type ClearanceReason = (typeof CLEARANCE_REASON)[keyof typeof CLEARANCE_REASON] | DockingPolicyReason

/**
 * The docking-rights hook (S-M2-11): `canDock(station)` returns `CLEARANCE_REASON.none` to allow, else a
 * `DOCKING_POLICY_REASON`. Called once per clearance request (message rate), after the range check.
 */
export interface DockingPolicy {
  canDock(station: number): ClearanceReason
}

let policy: DockingPolicy | null = null

/** Installs (or, with null, removes) the docking policy every clearance request consults. */
export function installDockingPolicy(p: DockingPolicy | null): void {
  policy = p
}

/** The installed docking policy (tests, the debug API). */
export function dockingPolicy(): DockingPolicy | null {
  return policy
}

export interface ClearanceState {
  status: ClearanceStatus
  /** Station index of the current or last grant; -1 before any. */
  station: number
  /** Game time of the grant and of its expiry (seconds). */
  grantedAt: number
  expiresAt: number
}

export function createClearanceState(): ClearanceState {
  return { status: CLEARANCE_STATUS.none, station: -1, grantedAt: 0, expiresAt: 0 }
}

/**
 * Requests clearance at `station` from `distance` metres off its hull (centre distance less the station radius) at game time `now`. Returns `CLEARANCE_REASON.none`
 * when granted (the state then holds the grant), else the refusal (`tooFar`, or the installed policy's reason),
 * leaving the state unchanged.
 */
export function requestClearance(
  state: ClearanceState,
  station: number,
  distance: number,
  now: number,
  cfg: Pick<DockingConfig, 'requestRange' | 'clearanceSeconds'>,
): ClearanceReason {
  if (!(distance <= cfg.requestRange)) return CLEARANCE_REASON.tooFar
  if (policy !== null) {
    const refused = policy.canDock(station)
    if (refused !== CLEARANCE_REASON.none) return refused
  }
  state.status = CLEARANCE_STATUS.granted
  state.station = station
  state.grantedAt = now
  state.expiresAt = now + cfg.clearanceSeconds
  return CLEARANCE_REASON.none
}

/** Advances to game time `now`. True on the call where a grant runs out (the state becomes `expired`). */
export function stepClearance(state: ClearanceState, now: number): boolean {
  if (state.status !== CLEARANCE_STATUS.granted || now < state.expiresAt) return false
  state.status = CLEARANCE_STATUS.expired
  return true
}

/** True while `station` holds a live grant. */
export function hasClearance(state: Readonly<ClearanceState>, station: number): boolean {
  return state.status === CLEARANCE_STATUS.granted && state.station === station
}

/** Seconds of the grant left at `now` (0 without a live grant). */
export function clearanceLeft(state: Readonly<ClearanceState>, now: number): number {
  if (state.status !== CLEARANCE_STATUS.granted) return 0
  const left = state.expiresAt - now
  return left > 0 ? left : 0
}

/** Drops any grant (docked, launched, died, loaded): back to `none`. */
export function releaseClearance(state: ClearanceState): void {
  state.status = CLEARANCE_STATUS.none
  state.station = -1
  state.grantedAt = 0
  state.expiresAt = 0
}
